import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq } from "drizzle-orm";
import { ArrowLeft, Gauge, Pencil, RotateCcw, Timer, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useConfirm } from "@/components/ConfirmDialog";
import { CameraPreview } from "@/components/timer/CameraPreview";
import { ConfirmHeatPanel } from "@/components/timer/ConfirmHeatPanel";
import { HeatResults } from "@/components/timer/HeatResults";
import { HeatSetup } from "@/components/timer/HeatSetup";
import { TimerConnectionBar } from "@/components/timer/TimerConnectionBar";
import { TimerControls } from "@/components/timer/TimerControls";
import { getDb } from "@/db";
import {
	cars,
	events,
	heatAssignments,
	heats,
	laneRecords,
	runs,
} from "@/db/schema";
import { logAudit } from "@/lib/audit";
import { getSession } from "@/lib/auth-session";
import { type LiveScene, updateLiveState } from "@/lib/live-state";
import { useTimer } from "@/lib/timer/use-timer";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Server functions ────────────────────────────────────────────────

const getEventRaceData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const recentRuns = await db
			.select()
			.from(runs)
			.where(eq(runs.eventId, event.id))
			.orderBy(desc(runs.createdAt))
			.limit(20);

		const allRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, event.id), eq(runs.status, "valid")));

		const runCounts: Record<number, number> = {};
		for (const run of allRuns) {
			runCounts[run.carId] = (runCounts[run.carId] || 0) + 1;
		}

		// Get heat queue data
		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, event.id));

		const scheduledHeats = eventHeats
			.filter((h) => h.status === "scheduled")
			.sort((a, b) => a.heatNumber - b.heatNumber)
			.slice(0, 5);

		const completedHeats = eventHeats
			.filter((h) => h.status === "complete")
			.sort((a, b) => b.heatNumber - a.heatNumber)
			.slice(0, 3);
		const completedCount = eventHeats.filter(
			(h) => h.status === "complete",
		).length;
		const totalCount = eventHeats.length;

		type HeatQueueItem = {
			heatId: number;
			heatNumber: number;
			division: string | null;
			lanes: Array<{ lane: number; carId: number }>;
		};

		const heatQueue: HeatQueueItem[] = [];
		for (const heat of scheduledHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			heatQueue.push({
				heatId: heat.id,
				heatNumber: heat.heatNumber,
				division: heat.division,
				lanes: ha
					.map((a) => ({ lane: a.lane, carId: a.carId }))
					.sort((a, b) => a.lane - b.lane),
			});
		}

		const recentlyCompleted: HeatQueueItem[] = [];
		for (const heat of completedHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			recentlyCompleted.push({
				heatId: heat.id,
				heatNumber: heat.heatNumber,
				division: heat.division,
				lanes: ha
					.map((a) => ({ lane: a.lane, carId: a.carId }))
					.sort((a, b) => a.lane - b.lane),
			});
		}

		return {
			event,
			cars: eventCars,
			recentRuns,
			runCounts,
			heatQueue,
			recentlyCompleted,
			heatProgress: { completed: completedCount, total: totalCount },
		};
	});

const recordRun = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			carId: number;
			lane: number;
			elapsedTimeMs: number | null;
			status: string;
			heatId?: number | null;
		}) => {
			if (!Number.isInteger(data.eventId) || data.eventId <= 0)
				throw new Error("Invalid eventId");
			if (!Number.isInteger(data.carId) || data.carId <= 0)
				throw new Error("Invalid carId");
			if (!Number.isInteger(data.lane) || data.lane < 1)
				throw new Error("Invalid lane");
			return data;
		},
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();
		const car = await db
			.select()
			.from(cars)
			.where(eq(cars.id, data.carId))
			.get();
		if (!car || car.eventId !== data.eventId)
			throw new Error("Car not found in event");

		return db
			.insert(runs)
			.values({
				eventId: data.eventId,
				carId: data.carId,
				lane: data.lane,
				elapsedTimeMs: data.elapsedTimeMs,
				weightAtRunOz: car.weightOz ?? null,
				status: data.status,
				heatId: data.heatId ?? null,
			})
			.returning();
	});

const recordHeat = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			heatId?: number | null;
			lanes: Array<{
				carId: number;
				lane: number;
				elapsedTimeMs: number | null;
				status: string;
			}>;
		}) => {
			if (!Number.isInteger(data.eventId) || data.eventId <= 0)
				throw new Error("Invalid eventId");
			if (!Array.isArray(data.lanes) || data.lanes.length === 0)
				throw new Error("No lanes provided");
			for (const lane of data.lanes) {
				if (!Number.isInteger(lane.carId) || lane.carId <= 0)
					throw new Error("Invalid carId");
				if (!Number.isInteger(lane.lane) || lane.lane < 1)
					throw new Error("Invalid lane");
			}
			return data;
		},
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();

		// Look up cars for this event and verify ownership
		const carsData = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, data.eventId));
		const carMap = new Map(carsData.map((c) => [c.id, c]));

		for (const lane of data.lanes) {
			if (!carMap.has(lane.carId))
				throw new Error(`Car ${lane.carId} not found in event`);
		}

		// Insert all runs
		const values = data.lanes.map((lane) => ({
			eventId: data.eventId,
			carId: lane.carId,
			lane: lane.lane,
			elapsedTimeMs: lane.elapsedTimeMs,
			weightAtRunOz: carMap.get(lane.carId)?.weightOz ?? null,
			status: lane.status,
			heatId: data.heatId ?? null,
		}));

		const insertedRuns = await db.insert(runs).values(values).returning();

		// Detect and upsert lane records
		const newRecords: Array<{
			lane: number;
			division?: string | null;
		}> = [];

		for (const run of insertedRuns) {
			if (run.status !== "valid" || run.elapsedTimeMs == null) continue;
			const car = carMap.get(run.carId);
			if (!car) continue;

			// Check overall record (division = null)
			const divisions: Array<string | null> = [null];
			if (car.division) divisions.push(car.division);

			for (const div of divisions) {
				const existing = await db
					.select()
					.from(laneRecords)
					.where(
						and(
							eq(laneRecords.eventId, data.eventId),
							eq(laneRecords.lane, run.lane),
							div == null
								? eq(laneRecords.division, "") // SQLite: compare null via IS NULL below
								: eq(laneRecords.division, div),
						),
					)
					.get();

				// For null division, query differently
				let record: typeof existing;
				if (div == null) {
					const allForLane = await db
						.select()
						.from(laneRecords)
						.where(
							and(
								eq(laneRecords.eventId, data.eventId),
								eq(laneRecords.lane, run.lane),
							),
						);
					record = allForLane.find((r) => r.division == null);
				} else {
					record = existing;
				}

				if (!record || run.elapsedTimeMs < record.elapsedTimeMs) {
					if (record) {
						// Update existing record
						await db
							.update(laneRecords)
							.set({
								carId: run.carId,
								runId: run.id,
								elapsedTimeMs: run.elapsedTimeMs,
							})
							.where(eq(laneRecords.id, record.id));
					} else {
						// Insert new record
						await db.insert(laneRecords).values({
							eventId: data.eventId,
							lane: run.lane,
							carId: run.carId,
							runId: run.id,
							elapsedTimeMs: run.elapsedTimeMs,
							division: div,
						});
					}
					newRecords.push({ lane: run.lane, division: div });
				}
			}
		}

		logAudit(data.eventId, "record_heat", {
			lanes: data.lanes.map((l) => ({
				carId: l.carId,
				lane: l.lane,
				time: l.elapsedTimeMs,
				status: l.status,
			})),
		});

		return { runs: insertedRuns, newRecords };
	});

const getNextHeat = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const scheduledHeats = await db
			.select()
			.from(heats)
			.where(
				and(eq(heats.eventId, Number(eventId)), eq(heats.status, "scheduled")),
			);

		if (scheduledHeats.length === 0) return null;

		// Get the lowest heat number
		const nextHeat = scheduledHeats.sort(
			(a, b) => a.heatNumber - b.heatNumber,
		)[0];

		const assignments = await db
			.select()
			.from(heatAssignments)
			.where(eq(heatAssignments.heatId, nextHeat.id));

		return {
			heatId: nextHeat.id,
			heatNumber: nextHeat.heatNumber,
			division: nextHeat.division,
			assignments: assignments.map((a) => ({
				lane: a.lane,
				carId: a.carId,
			})),
		};
	});

const completeHeat = createServerFn({ method: "POST" })
	.inputValidator((heatId: number) => {
		if (!Number.isInteger(heatId) || heatId <= 0)
			throw new Error("Invalid heatId");
		return heatId;
	})
	.handler(async ({ data: heatId }) => {
		await requireAdmin();
		await getDb()
			.update(heats)
			.set({ status: "complete" })
			.where(eq(heats.id, heatId));
	});

const redoHeat = createServerFn({ method: "POST" })
	.inputValidator((data: { heatId: number; eventId: number }) => {
		if (!Number.isInteger(data.heatId) || data.heatId <= 0)
			throw new Error("Invalid heatId");
		if (!Number.isInteger(data.eventId) || data.eventId <= 0)
			throw new Error("Invalid eventId");
		return data;
	})
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();

		// Verify the heat belongs to the event and is complete
		const heat = await db
			.select()
			.from(heats)
			.where(and(eq(heats.id, data.heatId), eq(heats.eventId, data.eventId)))
			.get();
		if (!heat) throw new Error("Heat not found");
		if (heat.status !== "complete") throw new Error("Heat is not complete");

		// Invalidate all runs linked to this heat
		await db
			.update(runs)
			.set({ status: "invalid" })
			.where(
				and(eq(runs.heatId, data.heatId), eq(runs.eventId, data.eventId)),
			);

		// Reset heat back to scheduled
		await db
			.update(heats)
			.set({ status: "scheduled" })
			.where(eq(heats.id, data.heatId));

		logAudit(data.eventId, "redo_heat", {
			heatId: data.heatId,
			heatNumber: heat.heatNumber,
		});
	});

const invalidateRun = createServerFn({ method: "POST" })
	.inputValidator((data: { runId: number; eventId: number }) => {
		if (!Number.isInteger(data.runId) || data.runId <= 0)
			throw new Error("Invalid runId");
		if (!Number.isInteger(data.eventId) || data.eventId <= 0)
			throw new Error("Invalid eventId");
		return data;
	})
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(runs)
			.set({ status: "invalid" })
			.where(and(eq(runs.id, data.runId), eq(runs.eventId, data.eventId)));
		logAudit(data.eventId, "invalidate_run", { runId: data.runId });
	});

const updateRun = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			runId: number;
			eventId: number;
			elapsedTimeMs: number | null;
			status: string;
		}) => {
			if (!Number.isInteger(data.runId) || data.runId <= 0)
				throw new Error("Invalid runId");
			if (!Number.isInteger(data.eventId) || data.eventId <= 0)
				throw new Error("Invalid eventId");
			return data;
		},
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(runs)
			.set({ elapsedTimeMs: data.elapsedTimeMs, status: data.status })
			.where(and(eq(runs.id, data.runId), eq(runs.eventId, data.eventId)));
		logAudit(data.eventId, "update_run", {
			runId: data.runId,
			elapsedTimeMs: data.elapsedTimeMs,
			status: data.status,
		});
	});

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/events/$eventId/race")({
	loader: ({ params }) => getEventRaceData({ data: params.eventId }),
	component: RaceOperationPage,
});

// ── Helpers ─────────────────────────────────────────────────────────

function formatTime(ms: number | null): string {
	if (ms == null) return "--";
	return `${(ms / 1000).toFixed(3)}s`;
}

type RaceMode = "timer" | "manual";

// ── Main page ───────────────────────────────────────────────────────

function RaceOperationPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const queryClient = useQueryClient();
	const [confirm, confirmDialog] = useConfirm();

	const { data } = useQuery({
		queryKey: ["race", params.eventId],
		queryFn: () => getEventRaceData({ data: params.eventId }),
		initialData,
		refetchInterval: 5000,
	});

	const {
		event,
		cars: carList,
		recentRuns,
		runCounts,
		heatQueue,
		recentlyCompleted,
		heatProgress,
	} = data;

	// Mode persists in localStorage
	const [mode, setMode] = useState<RaceMode>(() => {
		if (typeof window === "undefined") return "manual";
		return (localStorage.getItem("race-mode") as RaceMode) || "manual";
	});

	const handleModeChange = (newMode: RaceMode) => {
		setMode(newMode);
		localStorage.setItem("race-mode", newMode);
	};

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			{confirmDialog}
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<h1 className="text-4xl text-foreground mb-2">Race Timing</h1>
				<p className="text-muted-foreground mb-4">{event.name}</p>

				{/* Mode toggle */}
				<div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 w-fit">
					<button
						type="button"
						onClick={() => handleModeChange("timer")}
						className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${
							mode === "timer"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						<Gauge size={14} />
						Timer
					</button>
					<button
						type="button"
						onClick={() => handleModeChange("manual")}
						className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-bold transition-colors ${
							mode === "manual"
								? "bg-card text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						<Timer size={14} />
						Manual
					</button>
				</div>

				{/* Heat queue */}
				{heatQueue.length > 0 && (
					<div className="bg-card rounded-xl p-4 mb-6">
						<div className="flex items-center justify-between mb-3">
							<h3 className="text-sm font-bold text-muted-foreground uppercase">
								Heat Queue
							</h3>
							{heatProgress.total > 0 && (
								<div className="flex items-center gap-2">
									<div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
										<div
											className="h-full bg-success rounded-full transition-all"
											style={{
												width: `${Math.round((heatProgress.completed / heatProgress.total) * 100)}%`,
											}}
										/>
									</div>
									<span className="text-xs text-muted-foreground">
										{heatProgress.completed}/{heatProgress.total}
									</span>
								</div>
							)}
						</div>
						<div className="space-y-2">
							{heatQueue.map((heat, i) => (
								<div
									key={heat.heatId}
									className={`rounded-lg px-3 py-2 text-sm ${
										i === 0
											? "bg-primary/10 border border-primary/30"
											: "bg-background border border-border"
									}`}
								>
									<div className="flex items-center justify-between mb-1">
										<span className="font-bold text-foreground">
											{i === 0 ? "→ " : ""}Heat #{heat.heatNumber}
										</span>
										{heat.division && (
											<span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
												{heat.division}
											</span>
										)}
									</div>
									<div className="flex gap-2 text-xs text-muted-foreground">
										{heat.lanes.map((lane) => {
											const car = carList.find((c) => c.id === lane.carId);
											return (
												<span key={lane.lane}>
													L{lane.lane}:{" "}
													<span className="text-foreground">
														{car?.carName ??
															car?.driverName ??
															`#${car?.carNumber}`}
													</span>
												</span>
											);
										})}
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				{/* Recently completed heats — redo */}
				{recentlyCompleted.length > 0 && (
					<div className="bg-card/50 rounded-xl p-4 mb-6">
						<h3 className="text-sm font-bold text-muted-foreground uppercase mb-3">
							Completed
						</h3>
						<div className="space-y-2">
							{recentlyCompleted.map((heat) => (
								<div
									key={heat.heatId}
									className="rounded-lg px-3 py-2 text-sm bg-background border border-border flex items-center justify-between"
								>
									<div className="min-w-0">
										<span className="font-bold text-muted-foreground">
											Heat #{heat.heatNumber}
										</span>
										<div className="flex gap-2 text-xs text-muted-foreground mt-0.5">
											{heat.lanes.map((lane) => {
												const car = carList.find(
													(c) => c.id === lane.carId,
												);
												return (
													<span key={lane.lane}>
														L{lane.lane}:{" "}
														<span className="text-foreground">
															{car?.carName ??
																car?.driverName ??
																`#${car?.carNumber}`}
														</span>
													</span>
												);
											})}
										</div>
									</div>
									<button
										type="button"
										title="Invalidate runs and re-race this heat"
										onClick={async () => {
											const ok = await confirm({
												title: `Redo Heat #${heat.heatNumber}?`,
												description:
													"This will invalidate all runs from this heat and put it back in the queue.",
												confirmLabel: "Redo Heat",
												variant: "destructive",
											});
											if (!ok) return;
											await redoHeat({
												data: {
													heatId: heat.heatId,
													eventId: event.id,
												},
											});
											queryClient.invalidateQueries({
												queryKey: ["race", params.eventId],
											});
										}}
										className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors shrink-0 ml-2"
									>
										<RotateCcw size={12} />
										Redo
									</button>
								</div>
							))}
						</div>
					</div>
				)}

				{mode === "timer" ? (
					<TimerMode
						event={event}
						carList={carList}
						runCounts={runCounts}
						recentRuns={recentRuns}
						confirm={confirm}
						onDataChange={() =>
							queryClient.invalidateQueries({
								queryKey: ["race", params.eventId],
							})
						}
					/>
				) : (
					<ManualMode
						event={event}
						carList={carList}
						runCounts={runCounts}
						recentRuns={recentRuns}
						confirm={confirm}
						onDataChange={() =>
							queryClient.invalidateQueries({
								queryKey: ["race", params.eventId],
							})
						}
					/>
				)}
			</div>
		</div>
	);
}

// ── Timer mode ──────────────────────────────────────────────────────

interface ModeProps {
	event: {
		id: number;
		name: string;
		laneCount: number;
		attemptsAllowed: number;
	};
	carList: Array<{
		id: number;
		carName: string | null;
		driverName: string;
		carNumber: number;
		weightOz: number | null;
		photoUrl: string | null;
		avatarUrl: string | null;
		division: string | null;
		createdAt: Date;
		updatedAt: Date;
		eventId: number;
	}>;
	runCounts: Record<number, number>;
	recentRuns: Array<{
		id: number;
		eventId: number;
		carId: number;
		lane: number;
		elapsedTimeMs: number | null;
		status: string;
		createdAt: Date;
		weightAtRunOz: number | null;
	}>;
	onDataChange: () => void;
	confirm: ReturnType<typeof useConfirm>[0];
}

function TimerMode({
	event,
	carList,
	runCounts,
	recentRuns,
	onDataChange,
	confirm,
}: ModeProps) {
	const timer = useTimer();
	const [laneAssignments, setLaneAssignments] = useState<
		Map<number, number | null>
	>(new Map());
	const [recording, setRecording] = useState(false);
	const [lastRecordedCount, setLastRecordedCount] = useState<number | null>(
		null,
	);
	const [heatNumber, setHeatNumber] = useState(1);
	const [currentScheduleHeatId, setCurrentScheduleHeatId] = useState<
		number | null
	>(null);
	const [nextHeatLabel, setNextHeatLabel] = useState<string | null>(null);
	const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState<
		number | null
	>(null);
	const autoAdvanceTimerRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);

	// Clean up auto-advance timer on unmount
	useEffect(() => {
		return () => {
			if (autoAdvanceTimerRef.current) {
				clearInterval(autoAdvanceTimerRef.current);
				autoAdvanceTimerRef.current = null;
			}
		};
	}, []);

	// Camera calibration state
	const [calibrating, setCalibrating] = useState(false);
	const [cameraRegions, setCameraRegions] = useState<
		import("@/lib/timer/camera-transport").LaneRegion[]
	>([]);
	const [cameraSensitivity, setCameraSensitivity] = useState(0.3);

	// Sync regions to transport when they change
	useEffect(() => {
		if (timer.cameraTransport && cameraRegions.length > 0) {
			timer.cameraTransport.regions = cameraRegions;
		}
	}, [cameraRegions, timer.cameraTransport]);

	// Load regions + sensitivity from transport when camera connects
	useEffect(() => {
		if (timer.cameraTransport) {
			setCameraRegions(timer.cameraTransport.regions);
			setCameraSensitivity(timer.cameraTransport.sensitivity);
		}
	}, [timer.cameraTransport]);

	// Detect browser capabilities
	const [hasBluetooth, setHasBluetooth] = useState(false);
	const [hasSerial, setHasSerial] = useState(false);
	const [hasCamera, setHasCamera] = useState(false);

	useEffect(() => {
		setHasBluetooth(
			typeof navigator !== "undefined" && "bluetooth" in navigator,
		);
		setHasSerial(typeof navigator !== "undefined" && "serial" in navigator);
		setHasCamera(
			typeof navigator !== "undefined" &&
				!!navigator.mediaDevices?.getUserMedia,
		);
	}, []);

	const effectiveLaneCount = timer.gateMode === "sensor" ? 1 : event.laneCount;

	const assignLane = useCallback((lane: number, carId: number | null) => {
		setLaneAssignments((prev) => {
			const next = new Map(prev);
			if (carId == null) {
				next.delete(lane);
			} else {
				next.set(lane, carId);
			}
			return next;
		});
	}, []);

	const quickFill = useCallback(() => {
		// Auto-assign next unraced cars to empty lanes
		const assigned = new Set(laneAssignments.values());
		const available = carList
			.filter((c) => !assigned.has(c.id))
			.filter((c) => (runCounts[c.id] || 0) < event.attemptsAllowed)
			.sort((a, b) => (a.carName ?? "").localeCompare(b.carName ?? ""));

		setLaneAssignments((prev) => {
			const next = new Map(prev);
			let idx = 0;
			for (let lane = 1; lane <= effectiveLaneCount; lane++) {
				if (!next.get(lane) && idx < available.length) {
					next.set(lane, available[idx].id);
					idx++;
				}
			}
			return next;
		});
	}, [
		carList,
		effectiveLaneCount,
		event.attemptsAllowed,
		laneAssignments,
		runCounts,
	]);

	const loadNextHeat = useCallback(async () => {
		try {
			const next = await getNextHeat({
				data: String(event.id),
			});
			if (!next) {
				setNextHeatLabel(null);
				return;
			}
			const newAssignments = new Map<number, number | null>();
			for (const a of next.assignments) {
				newAssignments.set(a.lane, a.carId);
			}
			setLaneAssignments(newAssignments);
			setHeatNumber(next.heatNumber);
			setCurrentScheduleHeatId(next.heatId);
			setNextHeatLabel(
				`Heat #${next.heatNumber}${next.division ? ` (${next.division})` : ""}`,
			);
		} catch (err) {
			console.error("Failed to load next heat:", err);
		}
	}, [event.id]);

	// Auto-load first scheduled heat on mount
	useEffect(() => {
		loadNextHeat();
	}, [loadNextHeat]);

	const assignedLaneCount = Array.from(laneAssignments.values()).filter(
		(id) => id != null,
	).length;

	// Helper to push live state (fire-and-forget)
	const pushLiveState = useCallback(
		(scene: LiveScene) => {
			updateLiveState({ data: { eventId: event.id, scene } }).catch((err) =>
				console.error("Failed to push live state:", err),
			);
		},
		[event.id],
	);

	// Refs for tracking live state pushes
	const prevPushKeyRef = useRef("");
	const raceStartedAtRef = useRef(0);

	// Push race results to TV when lane results change or heat completes
	useEffect(() => {
		if (timer.laneResults.size === 0) return;

		// Build the lanes payload
		const racingLanes: Array<{
			lane: number;
			carId: number;
			elapsedTimeMs?: number | null;
			status?: string;
		}> = [];
		for (const [lane, carId] of laneAssignments) {
			if (carId == null) continue;
			const result = timer.laneResults.get(lane);
			racingLanes.push({
				lane,
				carId,
				elapsedTimeMs: result?.elapsed_ms,
				status: result?.status,
			});
		}

		// Deduplicate: only push if the payload actually changed
		const pushKey = racingLanes
			.map((l) => `${l.lane}:${l.status ?? ""}:${l.elapsedTimeMs ?? ""}`)
			.join("|");
		if (pushKey === prevPushKeyRef.current) return;
		prevPushKeyRef.current = pushKey;

		// Use the start time captured in handleStart
		if (raceStartedAtRef.current === 0) return;

		pushLiveState({
			scene: "racing",
			heat: heatNumber,
			lanes: racingLanes,
			startedAt: raceStartedAtRef.current,
		});
	}, [
		timer.laneResults,
		timer.timerState,
		timer.heatResults,
		laneAssignments,
		heatNumber,
		pushLiveState,
	]);

	// Reset stale state and load next heat (used for heat advancement, not initial load)
	const advanceToNextHeat = useCallback(async () => {
		setLastRecordedCount(null);
		prevPushKeyRef.current = "";
		raceStartedAtRef.current = 0;
		try {
			await timer.reset();
		} catch {
			// No transport connected yet — nothing to reset
		}
		await loadNextHeat();
		pushLiveState({ scene: "upcoming" });
	}, [timer, loadNextHeat, pushLiveState]);

	const handleArm = useCallback(async () => {
		setLastRecordedCount(null);
		prevPushKeyRef.current = "";
		raceStartedAtRef.current = 0;
		await timer.arm(assignedLaneCount);

		// Push lineup scene
		const lanes: Array<{ lane: number; carId: number }> = [];
		for (const [lane, carId] of laneAssignments) {
			if (carId != null) lanes.push({ lane, carId });
		}
		pushLiveState({ scene: "lineup", heat: heatNumber, lanes });
	}, [timer, assignedLaneCount, laneAssignments, heatNumber, pushLiveState]);

	const handleStart = useCallback(async () => {
		raceStartedAtRef.current = Date.now();
		await timer.start();

		// Push racing scene
		const lanes: Array<{ lane: number; carId: number }> = [];
		for (const [lane, carId] of laneAssignments) {
			if (carId != null) lanes.push({ lane, carId });
		}
		pushLiveState({
			scene: "racing",
			heat: heatNumber,
			lanes,
			startedAt: raceStartedAtRef.current,
		});
	}, [timer, laneAssignments, heatNumber, pushLiveState]);

	const handleReset = useCallback(async () => {
		setLastRecordedCount(null);
		await timer.reset();
		pushLiveState({ scene: "upcoming" });
	}, [timer, pushLiveState]);

	const handleRecord = useCallback(async () => {
		if (!timer.heatResults) return; // Ensure heat is complete
		setRecording(true);
		try {
			const lanes: Array<{
				carId: number;
				lane: number;
				elapsedTimeMs: number | null;
				status: string;
			}> = [];

			for (const [lane, carId] of laneAssignments) {
				if (carId == null) continue;
				const result = timer.laneResults.get(lane);
				if (!result) continue;

				lanes.push({
					carId,
					lane,
					elapsedTimeMs: result.status === "valid" ? result.elapsed_ms : null,
					status: result.status === "pending" ? "dnf" : result.status,
				});
			}

			if (lanes.length > 0) {
				const result = await recordHeat({
					data: {
						eventId: event.id,
						heatId: currentScheduleHeatId ?? null,
						lanes,
					},
				});
				setLastRecordedCount(lanes.length);
				onDataChange();

				// Mark scheduled heat as complete
				if (currentScheduleHeatId) {
					completeHeat({ data: currentScheduleHeatId }).catch((err) =>
						console.error("Failed to complete heat:", err),
					);
					setCurrentScheduleHeatId(null);
				}

				// Push results scene with new records info
				pushLiveState({
					scene: "results",
					heat: heatNumber,
					lanes: lanes.map((l) => ({
						...l,
						elapsedTimeMs: l.elapsedTimeMs,
						status: l.status,
					})),
					newRecords: result.newRecords,
				});

				// Pre-fetch next heat label and start auto-advance countdown
				getNextHeat({ data: String(event.id) })
					.then((next) => {
						if (next) {
							const label = `Heat #${next.heatNumber}${next.division ? ` (${next.division})` : ""}`;
							setNextHeatLabel(label);
							// Start 5-second auto-advance countdown
							setAutoAdvanceCountdown(5);
							if (autoAdvanceTimerRef.current)
								clearInterval(autoAdvanceTimerRef.current);
							autoAdvanceTimerRef.current = setInterval(() => {
								setAutoAdvanceCountdown((prev) => {
									if (prev === null || prev <= 1) {
										if (autoAdvanceTimerRef.current)
											clearInterval(autoAdvanceTimerRef.current);
										autoAdvanceTimerRef.current = null;
										// Trigger advance to next heat
										advanceToNextHeat();
										return null;
									}
									return prev - 1;
								});
							}, 1000);
						} else {
							setNextHeatLabel(null);
							// No schedule — just increment heat number for display
							setHeatNumber((n) => n + 1);
						}
					})
					.catch(() => {});
			}
		} catch (err) {
			console.error("Failed to record heat:", err);
		} finally {
			setRecording(false);
		}
	}, [
		event.id,
		laneAssignments,
		timer.laneResults,
		timer.heatResults,
		onDataChange,
		pushLiveState,
		heatNumber,
		currentScheduleHeatId,
		advanceToNextHeat,
	]);

	const handleDiscard = useCallback(async () => {
		setLastRecordedCount(null);
		await timer.reset();
		pushLiveState({ scene: "upcoming" });
	}, [timer, pushLiveState]);

	const handleRerun = useCallback(async () => {
		setLastRecordedCount(null);
		prevPushKeyRef.current = "";
		raceStartedAtRef.current = 0;
		await timer.arm(assignedLaneCount);

		// Push lineup scene for the re-run
		const lanes: Array<{ lane: number; carId: number }> = [];
		for (const [lane, carId] of laneAssignments) {
			if (carId != null) lanes.push({ lane, carId });
		}
		pushLiveState({ scene: "lineup", heat: heatNumber, lanes });
	}, [timer, assignedLaneCount, laneAssignments, heatNumber, pushLiveState]);

	const heatComplete = timer.heatResults != null;

	return (
		<div className="space-y-4">
			<TimerConnectionBar
				connectionState={timer.connectionState}
				transportName={timer.transportName}
				isDemo={timer.isDemo}
				gateMode={timer.gateMode}
				error={timer.error}
				hasBluetooth={hasBluetooth}
				hasSerial={hasSerial}
				hasCamera={hasCamera}
				onConnectBle={timer.connectBle}
				onConnectSerial={timer.connectSerial}
				onConnectMock={timer.connectMock}
				onConnectCamera={timer.connectCamera}
				onConnectHybridBle={timer.connectHybridBle}
				onConnectHybridSerial={timer.connectHybridSerial}
				onConnectHybridRemoteBle={timer.connectHybridRemoteBle}
				onConnectHybridRemoteSerial={timer.connectHybridRemoteSerial}
				onConnectRemoteCamera={timer.connectRemoteCamera}
				onDisconnect={timer.disconnect}
			/>

			{timer.cameraStream && timer.cameraTransport && (
				<CameraPreview
					stream={timer.cameraStream}
					transport={timer.cameraTransport}
					laneRegions={cameraRegions}
					onRegionsChange={setCameraRegions}
					laneResults={timer.laneResults}
					sensitivity={cameraSensitivity}
					onSensitivityChange={(v) => {
						setCameraSensitivity(v);
						if (timer.cameraTransport) timer.cameraTransport.sensitivity = v;
					}}
					calibrating={calibrating}
					onCalibrateToggle={() => setCalibrating((c) => !c)}
				/>
			)}

			{timer.remoteSessionCode && (
				<RemoteCameraPanel
					sessionCode={timer.remoteSessionCode}
					transport={timer.remoteTransport}
				/>
			)}

			<HeatSetup
				laneCount={effectiveLaneCount}
				laneAssignments={laneAssignments}
				cars={carList}
				runCounts={runCounts}
				attemptsAllowed={event.attemptsAllowed}
				onAssign={assignLane}
				onQuickFill={quickFill}
				onLoadNextHeat={nextHeatLabel ? advanceToNextHeat : undefined}
				nextHeatLabel={nextHeatLabel}
			/>

			<TimerControls
				timerState={timer.timerState}
				connectionState={timer.connectionState}
				hasLaneAssignments={assignedLaneCount > 0}
				gateMode={timer.gateMode}
				onArm={handleArm}
				onStart={handleStart}
				onReset={handleReset}
			/>

			{/* Recorded success banner */}
			{lastRecordedCount != null && (
				<div className="bg-success/20 border border-success rounded-xl p-3 text-center">
					<p className="text-foreground text-sm font-bold">
						{lastRecordedCount} run{lastRecordedCount !== 1 ? "s" : ""} recorded
					</p>
					{autoAdvanceCountdown !== null && nextHeatLabel && (
						<div className="flex items-center justify-center gap-2 mt-2">
							<p className="text-muted-foreground text-xs">
								Loading {nextHeatLabel} in {autoAdvanceCountdown}s...
							</p>
							<button
								type="button"
								onClick={() => {
									if (autoAdvanceTimerRef.current)
										clearInterval(autoAdvanceTimerRef.current);
									autoAdvanceTimerRef.current = null;
									setAutoAdvanceCountdown(null);
								}}
								className="text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded"
							>
								Cancel
							</button>
						</div>
					)}
				</div>
			)}

			<HeatResults
				laneCount={effectiveLaneCount}
				laneAssignments={laneAssignments}
				laneResults={timer.laneResults}
				heatComplete={heatComplete}
				cars={carList}
			/>

			{heatComplete && (
				<ConfirmHeatPanel
					laneResults={timer.laneResults}
					laneAssignments={laneAssignments}
					recording={recording}
					onRecord={handleRecord}
					onDiscard={handleDiscard}
					onRerun={handleRerun}
					confirm={confirm}
				/>
			)}

			{/* Announcement / Break */}
			<AnnouncementInput pushLiveState={pushLiveState} />

			{/* Recent runs (shared) */}
			<RecentRuns
				runs={recentRuns}
				carList={carList}
				onDataChange={onDataChange}
			/>
		</div>
	);
}

// ── Manual mode (preserved original UI) ─────────────────────────────

function ManualMode({
	event,
	carList,
	runCounts,
	recentRuns,
	onDataChange,
}: ModeProps) {
	const formId = useId();
	const timeInputRef = useRef<HTMLInputElement>(null);
	const pushManualResult = useCallback(
		(
			carId: number,
			lane: number,
			elapsedTimeMs: number | null,
			status: string,
		) => {
			updateLiveState({
				data: {
					eventId: event.id,
					scene: {
						scene: "results",
						heat: 0,
						lanes: [{ lane, carId, elapsedTimeMs, status }],
					},
				},
			}).catch((err) => console.error("Failed to push live state:", err));
		},
		[event.id],
	);

	const [selectedCarId, setSelectedCarId] = useState<number | "">("");
	const [lane, setLane] = useState(1);
	const [timeInput, setTimeInput] = useState("");
	const [isDnf, setIsDnf] = useState(false);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [lastRecorded, setLastRecorded] = useState<{
		carName: string;
		time: string;
		status: string;
	} | null>(null);

	const filteredCars = carList.filter(
		(car) =>
			(car.carName ?? "").toLowerCase().includes(search.toLowerCase()) ||
			car.driverName.toLowerCase().includes(search.toLowerCase()),
	);

	const sortedCars = [...filteredCars].sort((a, b) => {
		const aCount = runCounts[a.id] || 0;
		const bCount = runCounts[b.id] || 0;
		const aDone = aCount >= event.attemptsAllowed;
		const bDone = bCount >= event.attemptsAllowed;
		if (aDone !== bDone) return aDone ? 1 : -1;
		return (a.carName ?? "").localeCompare(b.carName ?? "");
	});

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!selectedCarId || loading) return;
		setLoading(true);

		let elapsedTimeMs: number | null = null;
		let status = "valid";

		if (isDnf) {
			status = "dnf";
		} else {
			const seconds = Number.parseFloat(timeInput);
			if (Number.isNaN(seconds) || seconds <= 0) {
				setLoading(false);
				return;
			}
			elapsedTimeMs = Math.round(seconds * 1000);
		}

		const selectedCar = carList.find((c) => c.id === selectedCarId);

		try {
			await recordRun({
				data: {
					eventId: event.id,
					carId: Number(selectedCarId),
					lane,
					elapsedTimeMs,
					status,
				},
			});

			setLastRecorded({
				carName: selectedCar?.carName ?? `Car #${selectedCar?.carNumber}`,
				time: isDnf ? "DNF" : formatTime(elapsedTimeMs),
				status,
			});

			// Push results to TV
			pushManualResult(Number(selectedCarId), lane, elapsedTimeMs, status);

			setTimeInput("");
			setIsDnf(false);
			onDataChange();
			setTimeout(() => timeInputRef.current?.focus(), 100);
		} catch (err) {
			console.error("Failed to record run:", err);
		} finally {
			setLoading(false);
		}
	}

	function handleRerun() {
		setTimeInput("");
		setIsDnf(false);
		setLastRecorded(null);
		setTimeout(() => timeInputRef.current?.focus(), 100);
	}

	const selectedCar = carList.find((c) => c.id === selectedCarId);
	const selectedCarRunCount = selectedCarId ? runCounts[selectedCarId] || 0 : 0;
	const selectedCarAtLimit = selectedCarRunCount >= event.attemptsAllowed;

	return (
		<div className="space-y-6">
			{/* Success banner */}
			{lastRecorded && (
				<div className="bg-success/20 border border-success rounded-xl p-4 flex items-center justify-between">
					<div>
						<p className="text-foreground font-bold">
							Recorded: {lastRecorded.carName}
						</p>
						<p className="text-muted-foreground text-sm font-display">
							{lastRecorded.time}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={handleRerun}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-success hover:bg-success/80 text-foreground rounded-lg transition-colors text-sm font-bold"
						>
							<RotateCcw size={14} />
							Run Again
						</button>
						<button
							type="button"
							onClick={() => {
								setLastRecorded(null);
								setSelectedCarId("");
								setSearch("");
							}}
							className="px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
						>
							New Car
						</button>
					</div>
				</div>
			)}

			{/* Entry form */}
			<form
				onSubmit={handleSubmit}
				className="bg-card rounded-xl p-4 sm:p-6 space-y-4"
			>
				{/* Car selection */}
				<div>
					<label
						htmlFor={`${formId}-car-search`}
						className="block text-sm mb-1"
					>
						Car
					</label>
					<input
						id={`${formId}-car-search`}
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search by car or driver name..."
						className="w-full px-4 py-3 mb-2 text-base"
					/>
					<div className="max-h-48 overflow-y-auto border border-border rounded-lg bg-background">
						{sortedCars.map((car) => {
							const count = runCounts[car.id] || 0;
							const atLimit = count >= event.attemptsAllowed;
							const isSelected = selectedCarId === car.id;
							return (
								<button
									key={car.id}
									type="button"
									onClick={() => {
										setSelectedCarId(car.id);
										setSearch("");
									}}
									className={`w-full text-left px-4 py-3 flex items-center justify-between border-b border-border/50 last:border-0 transition-colors ${
										isSelected
											? "bg-primary/20 border-primary/30"
											: "hover:bg-muted/50"
									} ${atLimit ? "opacity-50" : ""}`}
								>
									<div className="flex items-center gap-3 min-w-0">
										{car.avatarUrl ? (
											<img
												src={car.avatarUrl}
												alt=""
												className="w-8 h-8 rounded object-cover shrink-0"
											/>
										) : (
											<div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-muted-foreground text-xs font-bold shrink-0">
												{car.carName?.charAt(0) ?? "#"}
											</div>
										)}
										<div className="min-w-0">
											<p
												className={`font-bold truncate ${isSelected ? "text-primary" : "text-foreground"}`}
											>
												<span className="inline-flex items-center text-xs font-bold text-primary bg-primary/10 rounded px-1 py-0.5 mr-1">
													#{car.carNumber}
												</span>
												{car.carName}
											</p>
											<p className="text-muted-foreground text-xs truncate">
												{car.driverName}
											</p>
										</div>
									</div>
									<div className="shrink-0 ml-2">
										<div className="flex items-center gap-1.5">
											<div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
												<div
													className={`h-full rounded-full transition-all ${
														atLimit ? "bg-success" : "bg-primary"
													}`}
													style={{
														width: `${Math.min(100, (count / event.attemptsAllowed) * 100)}%`,
													}}
												/>
											</div>
											<span
												className={`text-xs font-display ${atLimit ? "text-success" : "text-muted-foreground"}`}
											>
												{count}/{event.attemptsAllowed}
											</span>
										</div>
									</div>
								</button>
							);
						})}
						{sortedCars.length === 0 && (
							<p className="px-4 py-3 text-muted-foreground text-sm">
								No cars found
							</p>
						)}
					</div>
				</div>

				{/* Selected car info */}
				{selectedCar && (
					<div
						className={`rounded-lg px-4 py-2 text-sm ${
							selectedCarAtLimit
								? "bg-primary/10 border border-primary/30 text-primary"
								: "bg-card text-muted-foreground"
						}`}
					>
						Selected:{" "}
						<strong className="text-foreground">
							{selectedCar.carName ?? `Car #${selectedCar.carNumber}`}
						</strong>{" "}
						&mdash; {selectedCarRunCount}/{event.attemptsAllowed} runs completed
						{selectedCarAtLimit && " (at limit)"}
					</div>
				)}

				{/* Lane + Time row */}
				<div className="grid grid-cols-2 gap-4">
					<div>
						<span className="block text-sm mb-1">Lane</span>
						<div
							className="grid gap-2"
							style={{
								gridTemplateColumns: `repeat(${Math.min(event.laneCount, 4)}, 1fr)`,
							}}
						>
							{Array.from({ length: event.laneCount }, (_, i) => (
								<button
									key={`lane-${i + 1}`}
									type="button"
									onClick={() => setLane(i + 1)}
									className={`py-3 rounded-lg font-bold text-center transition-colors ${
										lane === i + 1
											? "bg-primary text-primary-foreground"
											: "bg-secondary text-secondary-foreground hover:bg-secondary/80"
									}`}
								>
									{i + 1}
								</button>
							))}
						</div>
					</div>

					<div>
						<label htmlFor={`${formId}-time`} className="block text-sm mb-1">
							Time (seconds)
						</label>
						<input
							id={`${formId}-time`}
							ref={timeInputRef}
							type="number"
							step="0.001"
							min="0"
							value={timeInput}
							onChange={(e) => setTimeInput(e.target.value)}
							disabled={isDnf}
							required={!isDnf}
							className="w-full px-4 py-3 disabled:opacity-50 font-display text-2xl"
							placeholder="3.456"
						/>
					</div>
				</div>

				<label className="flex items-center gap-2 text-foreground/80 cursor-pointer py-1">
					<input
						type="checkbox"
						checked={isDnf}
						onChange={(e) => setIsDnf(e.target.checked)}
						className="w-5 h-5 rounded"
					/>
					<span className="text-sm font-medium">DNF (Did Not Finish)</span>
				</label>

				<button
					type="submit"
					disabled={loading || !selectedCarId}
					className="w-full py-4 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-lg uppercase flex items-center justify-center gap-2"
				>
					<Timer size={20} />
					{loading ? "Recording..." : "Record Run"}
				</button>
			</form>

			{/* Recent runs */}
			<RecentRuns
				runs={recentRuns}
				carList={carList}
				onDataChange={onDataChange}
			/>
		</div>
	);
}

// ── Recent runs (shared between modes) ──────────────────────────────

function RecentRuns({
	runs: recentRuns,
	carList,
	onDataChange,
}: {
	runs: ModeProps["recentRuns"];
	carList: ModeProps["carList"];
	onDataChange: () => void;
}) {
	const [editingRunId, setEditingRunId] = useState<number | null>(null);
	const [editTimeInput, setEditTimeInput] = useState("");

	async function handleInvalidate(runId: number, eventId: number) {
		await invalidateRun({ data: { runId, eventId } });
		onDataChange();
	}

	async function handleUpdate(run: ModeProps["recentRuns"][0]) {
		const seconds = Number.parseFloat(editTimeInput);
		if (Number.isNaN(seconds) || seconds <= 0) return;
		await updateRun({
			data: {
				runId: run.id,
				eventId: run.eventId,
				elapsedTimeMs: Math.round(seconds * 1000),
				status: "valid",
			},
		});
		setEditingRunId(null);
		setEditTimeInput("");
		onDataChange();
	}

	return (
		<>
			<h2 className="text-lg text-foreground mb-4">Recent Runs</h2>
			{recentRuns.length === 0 ? (
				<p className="text-muted-foreground">No runs recorded yet.</p>
			) : (
				<div className="space-y-2">
					{recentRuns.map((run) => {
						const car = carList.find((c) => c.id === run.carId);
						const isEditing = editingRunId === run.id;
						return (
							<div
								key={run.id}
								className={`flex items-center justify-between bg-card/50 border rounded-lg px-4 py-3 ${
									run.status !== "valid"
										? "border-destructive/50 opacity-60"
										: "border-border"
								}`}
							>
								<div className="flex items-center gap-3 min-w-0">
									<span className="text-foreground font-bold text-sm truncate">
										{car && (
											<span className="text-primary mr-1">
												#{car.carNumber}
											</span>
										)}
										{car?.carName ?? ""}
									</span>
									<span className="text-muted-foreground text-sm hidden sm:inline">
										({car?.driverName})
									</span>
									<span className="text-muted-foreground text-xs shrink-0">
										L{run.lane}
									</span>
								</div>
								<div className="flex items-center gap-2 shrink-0">
									{isEditing ? (
										<>
											<input
												type="number"
												step="0.001"
												min="0"
												value={editTimeInput}
												onChange={(e) => setEditTimeInput(e.target.value)}
												className="w-24 px-2 py-1 text-sm font-display"
												placeholder="3.456"
												ref={(el) => el?.focus()}
												onKeyDown={(e) => {
													if (e.key === "Enter") handleUpdate(run);
													if (e.key === "Escape") setEditingRunId(null);
												}}
											/>
											<button
												type="button"
												onClick={() => handleUpdate(run)}
												className="px-2 py-1 bg-primary text-primary-foreground text-xs font-bold rounded"
											>
												Save
											</button>
											<button
												type="button"
												onClick={() => setEditingRunId(null)}
												className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded"
											>
												Cancel
											</button>
										</>
									) : (
										<>
											<span className="text-foreground font-display font-bold">
												{formatTime(run.elapsedTimeMs)}
											</span>
											{run.status !== "valid" && (
												<span className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-destructive text-foreground">
													{run.status}
												</span>
											)}
											{run.status === "valid" && (
												<>
													<button
														type="button"
														onClick={() => {
															setEditingRunId(run.id);
															setEditTimeInput(
																run.elapsedTimeMs != null
																	? (run.elapsedTimeMs / 1000).toFixed(3)
																	: "",
															);
														}}
														className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
														title="Edit time"
													>
														<Pencil size={14} />
													</button>
													<button
														type="button"
														onClick={() =>
															handleInvalidate(run.id, run.eventId)
														}
														className="p-1.5 text-destructive hover:text-destructive/80 hover:bg-muted rounded transition-colors"
														title="Mark as invalid"
													>
														<X size={14} />
													</button>
												</>
											)}
										</>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</>
	);
}

// ── Announcement input ──────────────────────────────────────────────

function RemoteCameraPanel({
	sessionCode,
	transport,
}: {
	sessionCode: string;
	transport: import("@/lib/timer/remote-camera-transport").RemoteCameraTransport | null;
}) {
	const [phoneAlive, setPhoneAlive] = useState(false);
	const [phoneStale, setPhoneStale] = useState(false);

	// Poll transport heartbeat state
	useEffect(() => {
		if (!transport) return;
		const interval = setInterval(() => {
			setPhoneAlive(transport.phoneConnected);
			if (transport.phoneConnected && transport.lastPhoneHeartbeat > 0) {
				const age = Date.now() - transport.lastPhoneHeartbeat;
				setPhoneStale(age > 10_000); // No heartbeat in 10s = stale
			}
		}, 1000);
		return () => clearInterval(interval);
	}, [transport]);

	const origin = typeof window !== "undefined" ? window.location.origin : "";

	return (
		<div className="bg-card rounded-xl p-4 space-y-3">
			<div className="text-center space-y-2">
				<div className="flex items-center justify-center gap-2">
					<p className="text-sm font-bold text-foreground uppercase tracking-wide">
						Phone Camera Sensor
					</p>
					{phoneAlive && !phoneStale && (
						<span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-success/20 text-success font-bold">
							<span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
							Phone Connected
						</span>
					)}
					{phoneAlive && phoneStale && (
						<span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-status-finals/20 text-status-finals font-bold">
							<span className="w-1.5 h-1.5 rounded-full bg-status-finals" />
							Phone Offline?
						</span>
					)}
				</div>
				{!phoneAlive && (
					<p className="text-xs text-muted-foreground">
						Open this URL on the phone mounted at the finish line:
					</p>
				)}
				<div className="flex items-center justify-center gap-4">
					<QRCodeSVG
						value={`${origin}/sensor?s=${sessionCode}`}
						size={120}
						bgColor="transparent"
						fgColor="currentColor"
						className="text-foreground"
					/>
					<div className="text-left space-y-1">
						<p className="text-2xl font-bold text-primary tracking-widest font-mono">
							{sessionCode}
						</p>
						<p className="text-xs text-muted-foreground break-all">
							{origin}/sensor?s={sessionCode}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

function AnnouncementInput({
	pushLiveState,
}: {
	pushLiveState: (scene: LiveScene) => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);

	return (
		<div className="bg-card rounded-xl p-4">
			<h3 className="text-sm font-bold text-muted-foreground uppercase mb-2">
				TV Announcement
			</h3>
			<div className="flex gap-2">
				<input
					ref={inputRef}
					type="text"
					placeholder="Message to display on TV..."
					className="flex-1 px-3 py-2 text-sm"
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							const input = e.currentTarget;
							if (input.value.trim()) {
								pushLiveState({
									scene: "announcement",
									message: input.value.trim(),
								});
								input.value = "";
							}
						}
					}}
				/>
				<button
					type="button"
					onClick={() => {
						const input = inputRef.current;
						if (input?.value.trim()) {
							pushLiveState({
								scene: "announcement",
								message: input.value.trim(),
							});
							input.value = "";
						}
					}}
					className="px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg text-sm font-bold"
				>
					Send
				</button>
				<button
					type="button"
					onClick={() => pushLiveState({ scene: "upcoming" })}
					className="px-3 py-2 bg-muted text-muted-foreground hover:text-foreground rounded-lg text-sm"
				>
					Clear
				</button>
			</div>
		</div>
	);
}
